import { jsPDF } from "jspdf";
import { t } from "../translations";
import { createLogger } from "./logger";
const logger = createLogger("PrintLabelUtils");

// Function to load image as base64
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

const generatePage = (
  pdf,
  dish,
  chef,
  lang,
  fallbackTranslations,
  logoBase64
) => {
  const translations = {
    by: t("modals.by", lang),
    bestBy: t("form.best_by", lang),
    heatingInstructions: t("form.heating_instructions", lang),
    enjoyMeal: t("form.enjoy_meal", lang)
  };

  const expiryDays = dish.expirity || 3;
  const bestByDate = new Date();
  bestByDate.setDate(bestByDate.getDate() + expiryDays);
  const formattedDate = bestByDate.toLocaleDateString(
    lang === "fr" ? "fr-CA" : "en-CA",
    { year: "numeric", month: "2-digit", day: "2-digit" }
  );

  const brandColor = [0, 0, 0];
  const margin = 2; // Minimal margins for label maker
  const pageWidth = 66.04; // 2.6 inches wide
  const pageHeight = 35.56; // 1.4 inches tall

  let yPos = margin + 2; // Start text

  // Helper function to set text style
  const setTextStyle = (size, color, weight = "normal") => {
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    pdf.setFont("helvetica", weight);
  };

  // Line 1: Panichef logo (top left)
  if (logoBase64) {
    const logoWidth = 8; // 8mm wide
    const logoHeight = 8; // 8mm tall (square logo)
    try {
      pdf.addImage(
        logoBase64,
        "PNG",
        margin + 1,
        yPos - 1.5,
        logoWidth,
        logoHeight
      );
      yPos += logoHeight + 1; // Logo height + 1mm spacing

      // Add "Panichefs" text next to/below logo
      setTextStyle(7, brandColor, "bold");
      pdf.text("Panichefs", margin + 1, yPos);
      yPos += 3; // Extra spacing after text
    } catch (error) {
      logger.error("Error adding logo:", error);
      // Fallback to text if image fails
      setTextStyle(7, brandColor, "bold");
      pdf.text("Panichefs", margin + 1, yPos);
      yPos += 3;
    }
  } else {
    // Fallback to text branding if no logo
    setTextStyle(7, brandColor, "bold");
    pdf.text("Panichefs", margin + 1, yPos);
    yPos += 3;
  }

  // Line 2: Food name (bold, larger)
  setTextStyle(5, brandColor, "bold");
  const dishName = dish.name || "N/A";
  pdf.text(dishName, margin + 1, yPos);
  yPos += 3;

  // Line 3: Chef name
  setTextStyle(4, [0, 0, 0], "normal");
  const chefName =
    `${translations.by} ${chef.firstname || ""} ${chef.lastname || ""}`.trim();
  pdf.text(chefName, margin + 1, yPos);
  yPos += 2.5;

  // Line 4: Best by date
  setTextStyle(3, brandColor, "bold");
  pdf.text(
    `${translations.bestBy.toUpperCase()}: ${formattedDate}`,
    margin + 1,
    yPos
  );
  yPos += 2.5;

  // Line 5: Heating instructions header
  setTextStyle(3, brandColor, "bold");
  pdf.text(translations.heatingInstructions.toUpperCase(), margin + 1, yPos);
  yPos += 2;

  // Line 6: Instruction text (compact, wrap if needed)
  setTextStyle(3, [0, 0, 0], "normal");
  const instructions =
    dish.comment && dish.comment.trim() !== ""
      ? dish.comment.split("\n").filter((line) => line.trim() !== "")
      : [fallbackTranslations[lang].microwave];

  const instruction = instructions[0] || "Microwave 2-3 min";
  const wrappedText = pdf.splitTextToSize(
    instruction,
    pageWidth - 2 * margin - 2
  );
  pdf.text(wrappedText.slice(0, 2), margin + 1, yPos); // Max 2 lines
  yPos += wrappedText.length * 1.8;

  // Line 7: Enjoy meal footer
  setTextStyle(3.5, brandColor, "bold");
  pdf.text(
    translations.enjoyMeal,
    pageWidth - margin - 1,
    pageHeight - margin - 1.5,
    {
      align: "right"
    }
  );
};

export const printDishLabel = async (dish, chef) => {
  const fallbackTranslations = {
    en: {
      microwave: "Microwave: 2-3 minutes on high, stirring halfway",
      oven: "Oven: 350°F (175°C) for 15-20 minutes",
      stovetop: "Stovetop: Heat in pan over medium heat, stirring occasionally"
    },
    fr: {
      microwave:
        "Micro-ondes: 2-3 minutes à puissance élevée, en remuant à mi-chemin",
      oven: "Four: 175°C pendant 15-20 minutes",
      stovetop:
        "Cuisinière: Chauffer dans une poêle à feu moyen, en remuant de temps en temps"
    }
  };

  // Load logo
  let logoBase64 = null;
  try {
    logoBase64 = await loadImageAsBase64("/logo.png");
  } catch (error) {
    logger.warn("Could not load logo, using text fallback:", error);
  }

  const pdf = new jsPDF({
    orientation: "landscape", // Landscape for label maker
    unit: "mm",
    format: [35.56, 66.04] // Label maker format: 1.4" x 2.6" (35.56mm x 66.04mm)
  });

  // Determine chef's language preference (default to English if not specified)
  const chefLanguage = chef.language || chef.lang || "en";

  // Generate single page in chef's language
  generatePage(pdf, dish, chef, chefLanguage, fallbackTranslations, logoBase64);

  // Save with sanitized filename including language
  const sanitizedDishName = (dish.name || "dish")
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase();
  pdf.save(`${sanitizedDishName}_label_${chefLanguage}.pdf`);
};
